import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaChartLine, FaExchangeAlt, FaShieldAlt, FaGlobeAmericas } from 'react-icons/fa';

const SharesTradingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Shares Trading' }
  ];

  const features = [
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Algorithmic Trading",
      description: "Proprietary trading algorithms designed to identify market inefficiencies and capitalize on short-term opportunities."
    },
    {
      icon: <FaExchangeAlt className="w-8 h-8 text-secondary" />,
      title: "Global Market Access",
      description: "Direct access to major global exchanges and trading venues for optimal execution and diverse opportunity sets."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Risk Management",
      description: "Sophisticated risk controls with position limits, stop-loss mechanisms, and diversification requirements."
    },
    {
      icon: <FaGlobeAmericas className="w-8 h-8 text-secondary" />,
      title: "Cross-Asset Strategies",
      description: "Trading strategies that work across equities, ETFs, futures, and options for comprehensive market coverage."
    }
  ];

  const strategies = [
    {
      title: "Statistical Arbitrage",
      description: "Identifying and exploiting pricing inefficiencies between related securities through mathematical modeling."
    },
    {
      title: "Momentum Trading",
      description: "Capitalizing on the continuation of existing market trends with sophisticated entry and exit signals."
    },
    {
      title: "Event-Driven Trading",
      description: "Trading around corporate events like earnings, mergers, spinoffs, and other catalysts that create market opportunity."
    },
    {
      title: "Pairs Trading",
      description: "Market-neutral strategy involving simultaneous long and short positions in correlated securities."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Shares Trading Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Sophisticated Shares Trading Strategy
              </h2>
              <p className="text-lg text-gray-700">
                Our Shares Trading Fund employs advanced algorithmic trading strategies and quantitative models to generate consistent returns from global equity markets, regardless of market direction.
              </p>
              <p className="text-lg text-gray-700">
                Unlike traditional buy-and-hold approaches, we focus on capturing short to medium-term opportunities through sophisticated trading techniques, technological advantage, and disciplined risk management.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Speak to an Investment Advisor
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/trader-using-smartphone-stock-market-report-buy-sell-forex-trading_220873-8114.jpg_m0uqkt.jpg" 
                alt="Shares Trading Operation" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Trading Strategies
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our fund deploys multiple trading strategies that work synergistically to capture value across different market conditions.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
            {strategies.map((strategy, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md border-l-4 border-secondary"
              >
                <h4 className="text-xl font-heading font-semibold mb-3">{strategy.title}</h4>
                <p className="text-gray-600">{strategy.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-8">
              Fund Advantages
            </h3>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">$175M+</div>
                <div className="text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">28.3%</div>
                <div className="text-gray-300">Historical Annual Return</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">0.91</div>
                <div className="text-gray-300">Sharpe Ratio</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SharesTradingPage;
